import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useSession } from '../session'
import { resolveReturnTo } from '../auth/redirect'
import { jwtClient, publicClient } from '../trpc'
import { getFileServiceUrl, getFileMediaUrl } from '../config/runtime'
import { AvatarCropModal } from '../components/AvatarCropModal'
import { useLang, SUPPORTED_LANGS, type Lang } from '../i18n'

const LANG_LABELS: Record<Lang, string> = { en: 'English', pt: 'Português', de: 'Deutsch' }

export function ProfilePage() {
  const { account, token, isLoading, logout, refreshAccount } = useSession()
  const { t, lang, setLang } = useLang()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Return-to contract (ADR 0052): when an app (e.g. grow) sends the user here to edit their profile,
  // echo a "back to app" affordance so they can return when done (ISSUE-2026-06-23-010).
  const returnTo = resolveReturnTo(searchParams, '')
  const canReturn = returnTo.startsWith('http')

  const [name, setName] = useState('')
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null)
  const [cropFile, setCropFile] = useState<File | null>(null)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [status, setStatus] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const [pwStage, setPwStage] = useState<'idle' | 'code'>('idle')
  const [pwCode, setPwCode] = useState('')
  const [pwNew, setPwNew] = useState('')

  useEffect(() => {
    if (!isLoading && !account) navigate('/signin', { replace: true })
  }, [account, isLoading, navigate])

  useEffect(() => {
    if (account) {
      setName(account.name ?? '')
      setAvatarUrl(account.avatar ?? null)
    }
  }, [account])

  if (isLoading || !account) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-base-200">
        <span className="loading loading-spinner loading-lg" />
      </div>
    )
  }

  const saveProfile = async () => {
    setError(null)
    setSaving(true)
    try {
      await jwtClient(token).updateProfile.mutate({ name: name.trim() || undefined, preferredLanguage: lang })
      await refreshAccount()
      setStatus(t.saved)
      setTimeout(() => setStatus(null), 2000)
    } catch (err) {
      setError(err instanceof Error ? err.message : t.somethingWrong)
    } finally {
      setSaving(false)
    }
  }

  // Pick → crop (square) → upload the cropped blob. Crop-on-upload (ADR 0052).
  const onPickAvatar = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) setCropFile(file)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const uploadCroppedAvatar = async (blob: Blob) => {
    setCropFile(null)
    if (!token) return
    setUploading(true)
    setError(null)
    try {
      // The File Service buffer upload requires the content hash (rejects with 400 otherwise).
      const bytes = new Uint8Array(await blob.arrayBuffer())
      const digest = await crypto.subtle.digest('SHA-256', bytes)
      const hashSha256 = Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('')
      const form = new FormData()
      form.append('file', new File([blob], 'avatar.webp', { type: blob.type || 'image/webp' }))
      form.append('hashSha256', hashSha256)
      const res = await fetch(`${getFileServiceUrl()}/private/uploads/buffer`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      })
      if (!res.ok) throw new Error('Upload failed')
      const { fileId, url } = (await res.json()) as { fileId: string; url: string | null }
      await jwtClient(token).updateProfile.mutate({ avatarFileId: fileId, avatar: url ?? undefined })
      setAvatarUrl(url ?? getFileMediaUrl(fileId))
      await refreshAccount()
    } catch (err) {
      setError(err instanceof Error ? err.message : t.somethingWrong)
    } finally {
      setUploading(false)
    }
  }

  const startPasswordReset = async () => {
    setError(null)
    try {
      await publicClient.forgotPassword.mutate({ email: account.email })
      setPwStage('code')
      setStatus(t.resetCodeSent)
    } catch (err) {
      setError(err instanceof Error ? err.message : t.somethingWrong)
    }
  }

  const confirmPasswordReset = async () => {
    setError(null)
    try {
      await publicClient.confirmForgotPassword.mutate({ email: account.email, code: pwCode, newPassword: pwNew })
      setPwStage('idle')
      setPwCode('')
      setPwNew('')
      setStatus(t.passwordUpdated)
    } catch (err) {
      setError(err instanceof Error ? err.message : t.somethingWrong)
    }
  }

  const initials = (name || account.email || '?').charAt(0).toUpperCase()

  return (
    <div className="min-h-screen bg-base-200 py-10">
      <div className="mx-auto max-w-lg px-4 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/wa-logo-black-no-bg.svg" alt="WeApps" className="h-7 w-auto" />
            <h1 className="text-2xl font-bold">{t.yourAccount}</h1>
          </div>
          <div className="flex items-center gap-2">
            {canReturn && (
              <button className="btn btn-primary btn-sm" onClick={() => { window.location.href = returnTo }}>
                {t.returnToApp}
              </button>
            )}
            <button className="btn btn-ghost btn-sm" onClick={logout}>{t.signOut}</button>
          </div>
        </div>

        <div className="card bg-base-100 shadow-sm">
          <div className="card-body gap-5">
            <div className="flex items-center gap-4">
              <div className="avatar">
                <div className="h-20 w-20 rounded-full bg-base-300 overflow-hidden ring ring-base-200">
                  {avatarUrl ? (
                    <img src={avatarUrl} alt={name} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-3xl font-bold">{initials}</div>
                  )}
                </div>
              </div>
              <div className="min-w-0">
                <p className="font-semibold truncate">{name || account.email}</p>
                <p className="text-sm text-base-content/60 truncate">{account.email}</p>
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={onPickAvatar} />
                <button className="btn btn-outline btn-xs mt-2" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                  {uploading ? t.uploading : t.changePicture}
                </button>
              </div>
            </div>

            <label className="form-control">
              <span className="label-text">{t.name}</span>
              <input className="input input-bordered" value={name} onChange={(e) => setName(e.target.value)} />
            </label>

            <label className="form-control">
              <span className="label-text">{t.preferredLanguage}</span>
              <select className="select select-bordered" value={lang} onChange={(e) => setLang(e.target.value as Lang)}>
                {SUPPORTED_LANGS.map((l) => (
                  <option key={l} value={l}>{LANG_LABELS[l]}</option>
                ))}
              </select>
            </label>

            {error && <div className="alert alert-error py-2 text-sm"><span>{error}</span></div>}
            {status && <div className="alert alert-success py-2 text-sm"><span>{status}</span></div>}

            <button className="btn btn-primary" onClick={saveProfile} disabled={saving}>
              {saving ? <span className="loading loading-spinner loading-sm" /> : t.saveChanges}
            </button>
          </div>
        </div>

        <div className="card bg-base-100 shadow-sm">
          <div className="card-body gap-3">
            <h2 className="font-semibold">{t.passwordSection}</h2>
            {pwStage === 'idle' ? (
              <button className="btn btn-outline btn-sm w-fit" onClick={startPasswordReset}>{t.changePassword}</button>
            ) : (
              <div className="space-y-2">
                <input className="input input-bordered w-full" placeholder={t.resetCode} value={pwCode} onChange={(e) => setPwCode(e.target.value)} />
                <input className="input input-bordered w-full" type="password" placeholder={t.newPassword} value={pwNew} onChange={(e) => setPwNew(e.target.value)} />
                <button className="btn btn-primary btn-sm" onClick={confirmPasswordReset}>{t.setNewPassword}</button>
              </div>
            )}
          </div>
        </div>
      </div>

      {cropFile && (
        <AvatarCropModal file={cropFile} onSave={uploadCroppedAvatar} onCancel={() => setCropFile(null)} />
      )}
    </div>
  )
}
